<div class="user-info-sidebar" *ngIf="userId">
    @if (isLoading) {
        <div class="text-center py-4">
            <span class="spinner-border spinner-border-sm"></span>
            <p class="text-muted mt-2">Loading user details...</p>
        </div>
    } @else if (error) {
        <div class="text-center py-4">
            <p class="text-danger">{{ error }}</p>
        </div>
    } @else if (details) {
        <!-- Profile Card -->
        <div class="card">
            <div class="card-body p-3 pb-0">
                <div class="profile-info">
                    <div class="profile-avatar">
                        @if (details?.image_url) {
                            <img [src]="details.image_url" alt="avatar" class="avatar">
                        } @else {
                            <div class="avatar-circle">
                                <span class="avatar-initials">{{ getInitials() }}</span>
                            </div>
                        }
                    </div>
                    
                    <h4 class="profile-name">
                        {{ details.first_name }} {{ details.last_name }}
                    </h4>
                    
                    <div class="profile-email">
                        <span>{{ details.email }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Basic Details Card -->
        <div class="card">
            <div class="card-header">
                <h5 class="mb-0 fw-bold">Basic Details</h5>
            </div>

            <div class="card-body p-3 pt-2">
                <div class="personal-info">
                    <div class="info-item" title="Age">
                        <img src="assets/profile/ic_calendar.svg" alt="Age">
                        <span>{{ details.age ? details.age + ' years' : '-' }}</span>
                    </div>
                    <div class="info-item" title="Weight">
                        <img src="assets/profile/ic_weight.svg" alt="Weight">
                        <span>{{ details.weight_in_kilograms ? details.weight_in_kilograms + ' kg' : '-' }}</span>
                    </div>
                    <div class="info-item" title="Height">
                        <img src="assets/profile/ic_height.svg" alt="Height">
                        <span>{{ details.height_in_centimeters ? details.height_in_centimeters + ' cm' : '-' }}</span>
                    </div>
                    <div class="info-item" title="Location">
                        <img src="assets/profile/ic_location.svg" alt="Location">
                        <span>-</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Notes Card -->
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0 fw-bold">Notes</h5>
                <img src="assets/common/ic_arrow_forward_m_size.svg" 
                     alt="Notes" 
                     title="All notes" 
                     class="link-icon" 
                     (click)="openUserDetails('notes')">
            </div>
            
            <div class="card-body p-3">
                <app-user-notes 
                    [userId]="userId" 
                    [notes]="details?.notes" 
                    [maxNotes]="1">
                </app-user-notes>
            </div>
        </div>

        <!-- Activity Summary Card -->
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0 fw-bold">Activity Summary</h5>
                <img src="assets/common/ic_arrow_forward_m_size.svg" 
                     alt="Activity" 
                     title="All user activity" 
                     class="link-icon" 
                     (click)="openUserDetails('progress', 'workouts')">
            </div>
            <div class="card-body p-3">
                <app-user-activity 
                    [userId]="userId" 
                    [activities]="details?.training_activity" 
                    [maxActivities]="2"
                    [enableCommenting]="false">
                </app-user-activity>
            </div>
        </div>

        <!-- Measurements Card -->
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0 fw-bold">Measurement Snapshot</h5>
                <img src="assets/common/ic_arrow_forward_m_size.svg" 
                     alt="Measurement" 
                     title="All user measurements" 
                     class="link-icon" 
                     (click)="openUserDetails('progress', 'measurements')">
            </div>
            
            <div class="card-body p-3">
                <div class="row g-3">
                    <div class="col-12">
                        <div class="measurement-item">
                            <div class="measurement-label">Weight</div>
                            <div class="measurement-value">{{ details.weight_in_kilograms || '-' }}<span class="unit"> kg</span></div>
                            <div class="measurement-change" [ngClass]="getChangeClass(0)">
                                <i [class]="getChangeIcon(0)"></i> {{ formatChange(0, 'kg') }}
                            </div>
                            <div class="measurement-subtitle">Compared to initial value</div>
                        </div>
                    </div>
                    
                    <div class="col-12">
                        <div class="measurement-item">
                            <div class="measurement-label">Waist</div>
                            <div class="measurement-value">-<span class="unit"> cm</span></div>
                            <div class="measurement-change" [ngClass]="getChangeClass(0)">
                                <i [class]="getChangeIcon(0)"></i> {{ formatChange(0, 'cm') }}
                            </div>
                            <div class="measurement-subtitle">Compared to initial value</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Subscription Status Card -->
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0 fw-bold">Subscription Status</h5>
                <img src="assets/common/ic_arrow_forward_m_size.svg" 
                     alt="Subscription" 
                     title="User subscriptions" 
                     class="link-icon" 
                     (click)="openUserDetails('billing')">
            </div>
            
            <div class="card-body p-3">
                <div class="d-flex align-items-center gap-2 mb-3">
                    <span *ngIf="details.subscription_info?.package_name" class="badge bg-success-subtle text-success">
                        Subscribed
                    </span>
                    <span *ngIf="!details.subscription_info?.package_name" class="badge badge-not-subscribed">
                        Not subscribed
                    </span>
                    <span *ngIf="details.subscription_info?.package_name && details.subscription_info?.is_active_subscriber" class="badge bg-success-subtle text-success">
                        Renewal active
                    </span>
                    <span *ngIf="details.subscription_info?.package_name && !details.subscription_info?.is_active_subscriber" class="badge bg-danger-subtle text-danger">
                        Renewal inactive
                    </span>
                </div>
                
                <div class="mb-2">
                    <p class="text-muted mb-0 font-size-12">Active subscription</p>
                    <p class="fw-normal mb-0">{{ details.subscription_info?.package_name || 'No active subscription' }}</p>
                </div>
                
                <div class="mb-2">
                    <p *ngIf="details.subscription_info?.package_name && details.subscription_info?.is_active_subscriber" class="text-muted mb-0 font-size-12">Renewal date</p>
                    <p *ngIf="details.subscription_info?.package_name && !details.subscription_info?.is_active_subscriber" class="text-muted mb-0 font-size-12">Valid until</p>
                    <p *ngIf="!details.subscription_info?.package_name && !details.subscription_info?.is_active_subscriber" class="text-muted mb-0 font-size-12">Renewal date</p>

                    @if (details.subscription_info?.valid_until) {
                        <p class="fw-normal mb-0">{{ details.subscription_info.valid_until | dateFormat:'MMM dd, y' }}</p>
                    } @else {
                        <p class="fw-normal mb-0">No renewal date</p>
                    }
                </div>
            </div>
        </div>
    }
</div>

